import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Table } from 'antd'
import { filter, isNil } from 'lodash'
import PropTypes from 'prop-types'

import { usePrevious, useQueryParams, useWindowSize } from '../../../core/hooks'

import { StyledFullWidthTableContainer } from '../../../common/components/styled'

import { DISH_FIELDS_CONFIG, DISH_TABLE_SCROLL_VALUES } from '../../constants'
import { getAvailableDishTableFields } from '../../helpers'
import { useDishTableHotkeyHighlights } from '../../hooks'
import { createDishTableColumns } from '../../services'

import DishEditableRow from './dish-editable-row'

const DishTable = ({
  dishes,
  isLoading,
  taskStatus,
  isTaskTierOneDone,
  isTaskCurationDone,
  isTaskQaDone,
  isDisabled,
  isMenuFake,
}) => {
  const { shouldScrollToDish, setQueryParams } = useQueryParams()
  const { height: windowHeight } = useWindowSize()

  useDishTableHotkeyHighlights(false, taskStatus)

  const {
    allergenOptions,
    dietOptions,
    dishTypeOptions,
    courseTypeOptions,
    cuisineTypeOptions,
    ingredientOptions,
  } = useSelector(store => store.common.metadata.data)

  const availableDishFields = useMemo(
    () => getAvailableDishTableFields(dishes),
    [dishes],
  )

  const previousIsDisabled = usePrevious(isDisabled)
  const previousIsTaskTierOneDone = usePrevious(isTaskTierOneDone)
  const previousIsTaskCurationDone = usePrevious(isTaskCurationDone)
  const previousIsTaskQaDone = usePrevious(isTaskQaDone)

  const forceShouldCellUpdate = useMemo(
    () =>
      previousIsDisabled !== isDisabled ||
      previousIsTaskTierOneDone !== isTaskTierOneDone ||
      previousIsTaskCurationDone !== isTaskCurationDone ||
      previousIsTaskQaDone !== isTaskQaDone,
    [
      previousIsDisabled,
      isDisabled,
      previousIsTaskTierOneDone,
      isTaskTierOneDone,
      previousIsTaskCurationDone,
      isTaskCurationDone,
      previousIsTaskQaDone,
      isTaskQaDone,
    ],
  )

  const nonLockedDishes = useMemo(
    () => filter(dishes, dish => !dish.isLocked),
    [dishes],
  )

  const columns = useMemo(
    () =>
      createDishTableColumns({
        availableDishFields,
        taskStatus,
        isTaskTierOneDone,
        isTaskCurationDone,
        isTaskQaDone,
        dishes: nonLockedDishes,
        isDisabled,
        allergenOptions,
        dietOptions,
        treeTypeOptions: {
          [DISH_FIELDS_CONFIG.DISH_TYPE.value]: dishTypeOptions,
          [DISH_FIELDS_CONFIG.COURSE_TYPE.value]: courseTypeOptions,
          [DISH_FIELDS_CONFIG.CUISINE_TYPE.value]: cuisineTypeOptions,
        },
        ingredientOptions,
        isMenuFake,
        forceShouldCellUpdate,
      }),
    [
      availableDishFields,
      taskStatus,
      isTaskTierOneDone,
      isTaskCurationDone,
      isTaskQaDone,
      nonLockedDishes,
      isDisabled,
      allergenOptions,
      dietOptions,
      dishTypeOptions,
      courseTypeOptions,
      cuisineTypeOptions,
      ingredientOptions,
      isMenuFake,
      forceShouldCellUpdate,
    ],
  )

  const tableComponents = useMemo(
    () => ({
      body: {
        row: DishEditableRow,
      },
    }),
    [],
  )

  const tableScrollConfig = useMemo(
    () => ({
      y: isNil(windowHeight)
        ? DISH_TABLE_SCROLL_VALUES.HEIGHT
        : windowHeight - DISH_TABLE_SCROLL_VALUES.HEIGHT,
    }),
    [windowHeight],
  )

  useEffect(() => {
    if (shouldScrollToDish) {
      const [scrollContainer] = document.querySelectorAll(
        '.dishes-table .ant-table-body',
      )
      if (scrollContainer) {
        scrollContainer.scrollIntoView()
      }
    }
  }, [shouldScrollToDish])

  useEffect(() => {
    let clearOutShouldScrollTimeout
    if (!isNil(shouldScrollToDish)) {
      clearOutShouldScrollTimeout = setTimeout(
        () => setQueryParams({ shouldScrollToDish: null }),
        500,
      )
    }

    return () => clearTimeout(clearOutShouldScrollTimeout)
  }, [setQueryParams, shouldScrollToDish])

  return (
    <StyledFullWidthTableContainer>
      <Table
        size="small"
        components={tableComponents}
        className="dishes-table"
        pagination={false}
        dataSource={dishes}
        columns={columns}
        rowKey="id"
        scroll={tableScrollConfig}
        sticky
        loading={isLoading}
      />
    </StyledFullWidthTableContainer>
  )
}

DishTable.propTypes = {
  dishes: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isTaskTierOneDone: PropTypes.bool,
  isTaskCurationDone: PropTypes.bool,
  isTaskQaDone: PropTypes.bool,
  isMenuFake: PropTypes.bool,
  taskStatus: PropTypes.string.isRequired,
}

DishTable.defaultProps = {
  dishes: [],
  isLoading: false,
  isDisabled: false,
  isTaskTierOneDone: false,
  isTaskCurationDone: false,
  isTaskQaDone: false,
  isMenuFake: false,
}

export default DishTable
